import { ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { finalize } from 'rxjs/operators';
import { appModuleAnimation } from '@shared/animations/routerTransition';
import { TipoAlergiaList } from '@shared/model/tipo-alergia-enum.model';
import { PagedListingComponentBase, PagedRequestDto } from '@shared/paged-listing-component-base';
import { PacienteAlergiaDto, PacienteAlergiaServiceProxy, PacienteDto, PacienteServiceProxy, TipoAlergiaEnum, UpdatePacienteAlergiaDto } from '@shared/service-proxies/service-proxies';
import { CreatePacienteAlergiaComponent } from './create-paciente-alergia/create-paciente-alergia.component';
import { EditPacienteAlergiaComponent } from './edit-paciente-alergia/edit-paciente-alergia.component';
import { SelecionarDependenteDialogComponent } from '@app/meus-arquivos/selecionar-dependente-dialog/selecionar-dependente-dialog.component';
import { AppSessionService } from '@shared/session/app-session.service';

export class PagedPacienteAlergiaRequestDto extends PagedRequestDto {
  public substancia: string;
  public tipoAlergia: TipoAlergiaEnum;
  public pacienteId: string;
}

@Component({
  selector: 'app-default-paciente-alergias',
  templateUrl: './default-paciente-alergias.component.html',
  styleUrls: ['./default-paciente-alergias.component.scss'],
  animations: [appModuleAnimation()]
})
export class DefaultPacienteAlergiasComponent extends PagedListingComponentBase<PacienteAlergiaDto> implements OnInit {

  protected pacienteAlergias: PacienteAlergiaDto[] = [];
  protected tiposAlergias: TipoAlergiaList = new TipoAlergiaList();
  protected request: PagedPacienteAlergiaRequestDto = new PagedPacienteAlergiaRequestDto();
  pacienteAtual: PacienteDto;
  pacienteInicial: PacienteDto;
  pacienteId: string;
  dependentes: PacienteDto[] = [];

  constructor(
    injector: Injector,
    private readonly _pacienteAlergiaService: PacienteAlergiaServiceProxy,
    private readonly _pacienteServiceProxy: PacienteServiceProxy,
    private _appSessionService: AppSessionService,
    private readonly route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private _modalService: BsModalService
  ) { super(injector) }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const id = params['id'];
      this.request.pacienteId = id ?? this.appSession.pacienteId;
      this.refresh();
      this.getPaciente();
    });
  }

  getPaciente() {
    this._pacienteServiceProxy.getPacienteByUserId(this._appSessionService.userId).pipe(
      finalize(() => {
        abp.ui.clearBusy();
      })
    ).subscribe((paciente) => {
      this.pacienteAtual = paciente;
      this.pacienteInicial = paciente;
      if (paciente.cpf) {
        this.getDependentes(paciente.cpf);
      }
    })
  }

  protected list(request: PagedPacienteAlergiaRequestDto, pageNumber: number, finishedCallback: Function): void {
    abp.ui.setBusy();
    request.substancia = this.request.substancia;
    request.tipoAlergia = this.request.tipoAlergia;
    request.pacienteId = this.request.pacienteId;
    this._pacienteAlergiaService.getAll(
      request.pacienteId,
      request.substancia,
      request.tipoAlergia,
      request.skipCount,
      request.maxResultCount
    ).pipe(
      finalize(() => {
        abp.ui.clearBusy();
      })
    ).subscribe((result) => {
      this.pacienteAlergias = result.items;
      this.totalItems = result.totalCount;
      this.showPaging(result, pageNumber);
    });
  }

  protected delete(entity: PacienteAlergiaDto): void {
    abp.message.confirm(`Deseja mesmo remover a alergia ${entity.substancia}?`, "", (result) => {
      if (result) {
        abp.ui.setBusy();
        this._pacienteAlergiaService.delete(entity.id).pipe(
          finalize(() => {
            abp.ui.clearBusy();
          })
        ).subscribe(() => {
          abp.notify.success("Alergia removida com sucesso!");
          this.refresh();
        })
      }
    });
  }

  protected create(): void {
    const createModal = this._modalService.show(
      CreatePacienteAlergiaComponent,
      {
        class: 'modal-md',
        ignoreBackdropClick: true,
        initialState: {
          pacienteId: this.request.pacienteId
        }
      }
    );

    createModal.content.onSave.subscribe(() => {
      abp.notify.success("Alergia cadastrada com sucesso!");
      this.refresh();
    });
  }

  protected edit(entity: PacienteAlergiaDto): void {
    const updateModal = this._modalService.show(
      EditPacienteAlergiaComponent,
      {
        class: 'modal-md',
        ignoreBackdropClick: true,
        initialState: {
          pacienteAlergia: UpdatePacienteAlergiaDto.fromJS(entity.toJSON())
        }
      }
    );

    updateModal.content.onSave.subscribe(() => {
      abp.notify.success("Alergia atualizada com sucesso!");
      this.refresh();
    });
  }

  protected limparFiltros(): void {
    this.request.substancia = undefined;
    this.request.tipoAlergia = undefined;
    this.refresh();
  }

  getDependentes(cpf: string) {
    abp.ui.setBusy()
    this._pacienteServiceProxy.getDependentes(cpf).pipe(
      finalize(() => {
        abp.ui.clearBusy();
      })
    ).subscribe((dependentes) => {
      this.dependentes = dependentes;
      this.cdr.detectChanges();
    })
  }

  selecionarDependentes() {
    const selecionarDependenteDialog: BsModalRef = this._modalService.show(
      SelecionarDependenteDialogComponent,
      {
        class: 'modal-md',
        backdrop: 'static',
        keyboard: false,
        initialState: {
          dependentes: this.dependentes
        }
      }
    );

    selecionarDependenteDialog.content.onSelect.subscribe((id) => {
      this.pacienteAtual = this.dependentes.find(x => x.id === id);
      this.pacienteId = id;
      this.request.pacienteId = this.pacienteId;
      this.refresh();
    });
  }

  voltarParaPacienteInicial() {
    this.pacienteAtual = this.pacienteInicial;
    this.pacienteId = this.pacienteInicial.id;
    this.request.pacienteId = this.pacienteId;
    this.refresh();
  }
}
